import React, { useCallback } from 'react';
import classnames from 'clsx';

import { noop } from 'ui-components/default-props/noop';

import style from './react-table-wrapper.module.css';

/**
 * Renders a single react-table body row and its optional sub-row component.
 * @param {object} props
 * @param {import('../../../prop-types/react-table').ReactTableRowPropType} props.row
 * @param {import('react').HTMLAttributes<HTMLDivElement>} props.rowProps
 * @param {string} [props.className]
 * @param {(row: import('../../../prop-types/react-table').ReactTableRowPropType) => React.ReactNode} [props.renderSubRow]
 * @param {string} [props.cellClassName]
 * @param {(row: import('../../../prop-types/react-table').ReactTableRowPropType) => void} [props.onRowClick]
 * @returns {React.ReactElement}
 */
export const ReactTableBodyRow = ({ className, row, rowProps, renderSubRow, cellClassName, onRowClick = noop }) => {
  const handleClick = useCallback(() => onRowClick(row), [onRowClick, row]);

  return (
    <div className={classnames(style['react-table-body-row'], className)}>
      <div {...rowProps} onClick={handleClick}>
        {row.cells.map((cell) => {
          const { key: cellKey, ...cellProps } = cell.getCellProps() || {};

          return (
            <div
              key={cellKey}
              aria-labelledby={cell.column.id}
              className={classnames(style.cell, cell.column.className, cellClassName)}
              {...cellProps}
            >
              {cell.render('Cell')}
            </div>
          );
        })}
      </div>
      {renderSubRow?.(row)}
    </div>
  );
};

export const MemoizedReactTableBodyRow = React.memo(ReactTableBodyRow, (prevProps, nextProps) => {
  return (
    prevProps.row.original === nextProps.row.original &&
    prevProps.row.isSelected === nextProps.row.isSelected &&
    prevProps.row.isExpanded === nextProps.row.isExpanded &&
    prevProps.className === nextProps.className &&
    prevProps.cellClassName === nextProps.cellClassName &&
    prevProps.renderSubRow === nextProps.renderSubRow &&
    prevProps.onRowClick === nextProps.onRowClick
  );
});
